import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import AdaptedText from "../components/AdaptedText";
import PageHero from "../components/PageHero";
import WalkthroughPrompt from "../components/WalkthroughPrompt";
import StreakChip from "../components/StreakChip";
import WeeklyDigestCard from "../components/WeeklyDigestCard";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Database, Calculator, FolderOpen, Sparkles, ArrowRight, Sunrise } from "lucide-react";

const QUICK = [
  { to: "/databases", label: "DATABASES", icon: Database, desc: "Search and save partner lists", testid: "quick-databases" },
  { to: "/tools", label: "FINANCIAL TOOLS", icon: Calculator, desc: "Model pricing, margin, and value chain", testid: "quick-tools" },
  { to: "/downloads", label: "RESOURCES", icon: FolderOpen, desc: "Download templates and examples", testid: "quick-downloads" },
  { to: "/projects", label: "MY PROJECTS", icon: FolderOpen, desc: "Return to every saved item", testid: "quick-projects" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({
    modulesTotal: 16, modulesDone: 0, coursePct: 0,
    downloads: 0, savedWork: 0,
    certsEarned: 0, certsTotal: 0,
  });
  const [nextModule, setNextModule] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/progress/me").then((r) => r.data).catch(() => ({})),
      api.get("/downloads").then((r) => r.data.length).catch(() => 0),
      // Single source-of-truth for the "Saved work" + "Certificates" tiles.
      // Historically this component summed local `/planner` + `/projects`
      // list lengths (9), which disagreed with Workbook's own count (7)
      // for the same user. `/counts/summary` returns the canonical numbers
      // from persisted `worksheet_responses` + `issued_certificates`.
      api.get("/counts/summary").then((r) => r.data).catch(() => ({})),
      api.get("/modules").then((r) => r.data).catch(() => []),
    ]).then(([prog, downloads, summary, mods]) => {
      const total = (prog.core_total || 0) + (prog.bonus_total || 0);
      const done  = (prog.completed_slugs || []).length;
      const coursePct = total > 0 ? Math.round((done / total) * 100) : 0;
      setCounts({
        modulesTotal: total || 16, modulesDone: done, coursePct,
        downloads,
        savedWork: summary.saved_work ?? 0,
        certsEarned: summary.certs_earned ?? 0,
        certsTotal: summary.certs_total ?? 0,
      });
      // Suggest the first uncompleted core module (sorted by `order`), then
      // fall back to first uncompleted bonus, then null (all done).
      const completed = new Set(prog.completed_slugs || []);
      const passed    = new Set(prog.passed_slugs || []);
      const isDone = (s) => completed.has(s) || passed.has(s);
      const core = (mods || []).filter((m) => m.track === "core")
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const bonus = (mods || []).filter((m) => m.track === "bonus")
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const nxt = core.find((m) => !isDone(m.slug))
                ?? bonus.find((m) => !isDone(m.slug))
                ?? null;
      setNextModule(nxt);
    });
  }, []);

  const first = (user?.name || "").split(" ")[0] || "there";
  const modulePct = counts.coursePct;
  const hasPlan = !!user?.package_slug;

  return (
    <AppShell title="Dashboard">
      <WalkthroughPrompt />
      <div className="w-full">
        <PageHero
          eyebrow="Your Workspace"
          icon={Sunrise}
          title={<>Good morning, <span className="text-grad">{first}</span></>}
          right={
            <span className="chip-amber !bg-shell-side !text-white" data-testid="dashboard-package-chip">
              {(user?.package_slug || "free").toUpperCase()} PACKAGE
            </span>
          }
        >
          <AdaptedText
            as="p" className="mt-3 text-muted-foreground max-w-2xl"
            source="dashboard_headline" sourceId="default" field="morning"
            originalText="Pick up where you left off or open the work your brand needs today."
          />
          <StreakChip />
        </PageHero>

        {!hasPlan && (
          <div className="mt-8 flex items-start gap-4 grad-band text-white p-6 rounded-2xl" data-testid="upgrade-banner">
            <Sparkles className="w-5 h-5 mt-0.5" />
            <div className="flex-1">
              <p className="font-display text-2xl">Unlock the full workspace</p>
              <p className="text-sm text-white/90 mt-1">Choose a plan to access modules, databases, tools, and downloads.</p>
            </div>
            <Link to="/account/subscription" className="rounded-full bg-white text-primary px-4 py-2 text-sm font-semibold hover:bg-white/90 transition" data-testid="dashboard-upgrade-cta">
              Choose plan
            </Link>
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Course complete" value={`${modulePct}%`} testid="stat-course" />
          <Stat label="Modules complete" value={`${counts.modulesDone} / ${counts.modulesTotal}`} testid="stat-modules" />
          <Stat label="Submitted work" value={counts.savedWork} testid="stat-saved" />
          <Stat label="Certificates" value={`${counts.certsEarned} / ${counts.certsTotal || counts.modulesTotal}`} testid="stat-certificates" />
        </div>

        <div className="mt-8">
          <WeeklyDigestCard />
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 rounded-2xl p-8 bg-shell-side text-white relative overflow-hidden" data-testid="continue-card">
            <span className="chip-amber !bg-shell-accent !text-shell-side">
              {counts.modulesDone === 0 ? "Get started" : counts.modulesDone >= counts.modulesTotal ? "You're done" : "Continue"}
            </span>
            <h3 className="font-display text-3xl mt-4" data-testid="continue-title">
              {nextModule ? nextModule.name : counts.modulesDone >= counts.modulesTotal ? "Every module complete" : "Course loading…"}
            </h3>
            <p className="mt-2 text-sm text-white/80" data-testid="continue-sub">
              {nextModule
                ? `${counts.modulesDone} of ${counts.modulesTotal} modules complete • ${counts.coursePct}% overall`
                : counts.modulesDone >= counts.modulesTotal
                  ? "Head to Progress & Certificates to claim your credentials."
                  : "Pull up your next module in a moment."}
            </p>
            <div className="mt-6 h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-shell-accent"
                style={{ width: `${counts.coursePct}%` }}
                data-testid="continue-progress-bar"
              />
            </div>
            {nextModule ? (
              <Link to={`/modules/${nextModule.slug}`} className="mt-6 inline-flex items-center gap-2 rounded-md bg-white text-shell-side px-4 py-2 text-sm font-semibold" data-testid="resume-module">
                Resume Module <ArrowRight className="w-4 h-4" />
              </Link>
            ) : counts.modulesDone >= counts.modulesTotal ? (
              <Link to="/progress" className="mt-6 inline-flex items-center gap-2 rounded-md bg-white text-shell-side px-4 py-2 text-sm font-semibold" data-testid="resume-module">
                View certificates <ArrowRight className="w-4 h-4" />
              </Link>
            ) : null}
          </div>
          <div className="surface p-6 flex flex-col" data-testid="next-milestone-card">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Next milestone</p>
            <p className="mt-2 text-sm">
              {nextModule
                ? `Watch the video, read the material, then pass the ${nextModule.name} quiz at 80%+ to unlock the next module.`
                : "Finish the Founder final exam to earn the Certified CPG Founder™ credential."}
            </p>
            <p className="font-display text-4xl mt-6">{counts.coursePct}%</p>
            <p className="text-xs text-muted-foreground">Course completion</p>
            <Link
              to={nextModule ? `/modules/${nextModule.slug}` : "/exam/final"}
              className="mt-auto inline-flex items-center justify-center rounded-md bg-shell-accent/20 text-shell-side px-4 py-2 text-sm font-semibold mt-6"
              data-testid="open-quiz"
            >
              {nextModule ? "Open module" : "Open final exam"}
            </Link>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl">Start or resume work</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK.map((q) => (
              <Link key={q.to} to={q.to} className="surface p-6 hover:-translate-y-0.5 transition-transform block" data-testid={q.testid}>
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-shell-side bg-shell-accent/25 rounded-md px-2 py-1 inline-block">{q.label}</span>
                <p className="mt-4 text-sm text-muted-foreground">{q.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-shell-side">
                  Open <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <p className="font-display text-3xl">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
